<template>
  <div class="chatEditor">
    <!-- E D I T O R -->
    <div class="w-full relative bottom-0 md:bottom-30 pt-0 md:pt-20 mt-0 md:mt-20">
      <!-- mention -->
      <!-- mention -->
      <div v-show="showMentions" ref="mentionRef" :class="isDarkMode ? 'bg-dark-800' : 'bg-white'">
        <template v-if="hasMentions">
          <div
            v-for="(item, index) in filteredMentions"
            :key="item.id"
            :ref="el => mentionRefs[index] = el"
            class="flex items-center py-5 px-10 cursor-pointer last:mb-0 no-scroll-bar last:rounded-b-10 text-14"
            :class="[
              isDarkMode ? 'text-grey-500' : 'text-dark-600',
              {'bg-selection font-semibold': navigatedMentionIndex === index},
              {'mention-project': item.label === 'pages'},
              {'mention-user': item.label === 'users'}
            ]"
            @mouseenter="onHover"
            @mouseleave="outHover(index, $event)"
            @click.stop="selectMention(item)"
          >
            <div v-if="item.label === 'users'">
              <MentionUser :user="item" />
            </div>
            <div v-else class="flex items-center">
              <img v-if="item.icon" class="w-20 h-20 mr-10" :src="require(`@/assets/images/icons/${item.icon}.png`)" alt="icon">
              <img v-else class="w-20 h-20 mr-10" src="@/assets/images/icons/purple_circle.png" alt="icon">
              <span> {{ item.title }} </span>
            </div>
          </div>
        </template>
      </div>
      <!-- emoji -->
      <div v-show="showEmoji" ref="emojiRef" :class="isDarkMode ? 'bg-dark-800' : 'bg-white'">
        <template v-if="hasResults">
          <div
            v-for="(item, index) in filteredItems"
            :key="item.id"
            :ref="el => emojiRefs[index] = el"
            class="px-20 py-10 cursor-pointer last:mb-0 no-scroll-bar first:rounded-t-4 last:rounded-b-4"
            :class="[isDarkMode ? 'text-grey-500' : 'text-grey-main', {'bg-selection': navigatedEmojiIndex === index}]"
            @mouseenter="onHover"
            @mouseleave="outHover(index, $event)"
            @click.stop="selectEmoji(item, editor)"
          >
            {{ item.char }} <span class="ml-5">:{{ item.name }}:</span>
          </div>
        </template>
        <div v-else class="mb-5 text-app-color rounded-5 px-8 py-4 cursor-pointer opacity-50">
          No Emoji found
        </div>
      </div>

      <!-- text area -->
      <div
        class="relative bg-book px-16 pt-13 pb-10 rounded-0 md:rounded-8 transition duration-150"
        :class="[isExpandedEditor ? 'max-h-400' : 'max-h-200', isFocusedEditor ? 'shadow-chat-active' : 'shadow-chat']"
      >
        <!-- emoji picker -->
        <EmojiPicker
          v-if="showEmojiPicker"
          ref="emojiPickerRef"
          v-clickOutside="closeEmojiPicker"
          class="md:mx-30"
          :class="{ '-top-280 left-0': isMobile }"
          @selectEmoji="insertEmoji"
          @click.stop
        />
        <!-- gif picker -->
        <GifPicker
          v-if="showGifPicker"
          ref="gifPickerRef"
          v-clickOutside="closeGifPicker"
          class="md:mx-60"
          :class="{ '-top-380 left-0': isMobile }"
          @selectGif="insertGif"
          @click.stop
        />

        <i
          v-if="isMobile && !isExpandedEditor"
          class="absolute right-25 z-10 icon-expand text-15 transition-all duration-300 opacity-30"
          @click.stop="expandEditor"
        />
        <i
          v-if="isMobile && isExpandedEditor"
          class="absolute right-25 z-10 icon-minimize text-15 transition-all duration-300 opacity-30"
          @click.stop="expandEditor"
        />

        <!-- editor -->
        <editor-content
          id="editor_chat"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="on"
          class="editor__content editor__chat h-full font-main text-canvas-color
                text-main flex flex-col flex-grow overflow-x-hidden overflow-y-auto mb-10"
          :class="[isExpandedEditor ? 'min-h-200 max-h-300' : 'min-h-60 max-h-120', { 'mr-22': isMobile }]"
          :editor="editor"
          @focus="isFocusedEditor = true"
        />

        <div class="flex justify-between items-center">
          <div class="flex items-center">
            <!-- attach file button -->
            <div class="flex cursor-pointer" @click="openImageUploader">
              <i
                class="icon-attach-file text-22 transition-all duration-300 opacity-30 hover:opacity-100"
                :class="isDarkMode ? 'text-app-color' : 'text-dark-500'"
              />
              <input ref="inputImageRef" class="hidden" type="file" @change="uploadImage">
            </div>
            <!-- show emoji button -->
            <i
              class="icon-smile ml-7 mr-10 cursor-pointer text-22 transition-all duration-300 hover:opacity-100"
              :class="[isDarkMode ? 'text-app-color' : 'text-dark-500', showEmojiPicker ? 'opacity-100' : 'opacity-30']"
              @click.stop="openEmojiPicker"
            />
            <!-- show gif button -->
            <span
              class="cursor-pointer font-bold text-17 transition-all duration-300 opacity-30 hover:opacity-100"
              :class="[isDarkMode ? 'text-app-color' : 'text-dark-500', showGifPicker ? 'opacity-100' : 'opacity-30']"
              @click.stop="openGifPicker"
            >
              GIF
            </span>
          </div>

          <!-- send button -->
          <ButtonModule
            text="Send"
            class="text-14 md:text-16 w-full font-normal max-w-100
            rounded-4 justify-center py-9 text-white bg-link-color md:hover:opacity-80 border-2 border-link-color"
            @click="sendMessage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'

import { useEditor, EditorContent } from '@tiptap/vue-3'
import { chatExtensions } from '@/tiptap/extensions'

import EmojiMixin from '@/mixins/EmojiMixin.js'
import MentionMixin from '@/mixins/MentionMixin.js'

import { chatService, bookService } from '@/services'
import designConfig from '@/core/design-config'

import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import EmojiPicker from '@/components/shared/EmojiPicker'
import GifPicker from '@/components/shared/GifPicker'
import { useRoute } from 'vue-router'
// import MentionModule from '@/components/shared/MentionModule'
import MentionUser from '@/components/workspace/MentionUser'

export default defineComponent({
  components: { ButtonModule, EditorContent, EmojiPicker, GifPicker, MentionUser },

  props: {
    shareWith: Array,
    projects: Array,
    chatId: Number,
    bookId: Number
  },

  emits: ['message', 'scrollToDown'],

  setup (props, { emit }) {
    const { state, getters, commit } = useStore()
    const route = useRoute()

    const emoji = EmojiMixin()
    const mention = MentionMixin('.chatEditor')

    let uploadedImages = []
    let existingImages = []

    const inputImageRef = ref(null)
    const showEmojiPicker = ref(false)
    const showGifPicker = ref(false)
    const gifPickerRef = ref(null)
    const emojiPickerRef = ref(null)
    const isExpandedEditor = ref(false)
    const isFocusedEditor = ref(false)

    const mentionUser = ref([])
    const selectedUsers = ref([])

    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)
    const user = computed(() => state.profile.user)
    const settings = computed(() => state.settings.settings)

    const isHeadquarters = computed(() => route.name === 'Headquarters')

    const editor = useEditor({
      onUpdate: () => {
        existingImages = checkImages(editor.value.getJSON())
      },
      onFocus: () => {
        isFocusedEditor.value = true
      },
      onBlur: () => {
        isFocusedEditor.value = false
      },
      extensions: [
        ...chatExtensions({
          emoji: {
            onEnter: emoji.onEnter,
            onKeyDown: emoji.onKeyDown,
            onChange: emoji.onChange,
            onExit: emoji.onExit,
            onFilter: emoji.onFilter
          },
          mention: {
            items: () => {
              let users = props.shareWith

              if (isHeadquarters.value) users = state.team.members

              const mappedProjects = props.projects.map((p) => ({ ...p, label: 'pages' }))
              const mappedUsers = users.map((u) => ({
                ...u,
                label: 'users',
                title: u.firstName,
                chatId: props.chatId,
                bookId: props.bookId
              }))

              return [...mappedProjects, ...mappedUsers]
            },
            onEnter: mention.onEnter,
            onKeyDown: mention.onKeyDown,
            onChange: mention.onChange,
            onExit: mention.onExit,
            onFilter: mention.onFilter
          },
          image: {
            uploadFunc: imageUploaded
          },
          chat: {
            enterHandler: sendMessage
          }
        })
      ],
      content: ''
    })

    function checkImages (json) {
      let nodes = []

      json.content
        .filter(item => item.type === 'paragraph' && item.content?.length)
        .map(item => item.content)
        .forEach(item => nodes.push(...item))

      nodes = nodes.filter(item => item.type === 'imageChat')

      return nodes.map(item => +item.attrs.id)
    }

    onBeforeUnmount(() => {
      editor.value.destroy()
    })

    const message = ref()

    function sendMessage () {
      if (!editor.value.isEmpty() && !mention.showMentions.value && !emoji.showEmoji.value) {
        const deletedImages = uploadedImages.filter(item => !existingImages.includes(item))

        editor.value.getJSON().content
          .filter(item => item.type === 'paragraph' && item.content?.length)
          .map(item => item.content)
          .forEach(item => {
            selectedUsers.value.push(...item)
          })

        emit('message', {
          text: editor.value.getHTML(),
          existingImages: existingImages,
          deletedImages: deletedImages
        })

        message.value = editor.value.getHTML()

        uploadedImages = []

        editor.value.chain().focus().selectAll().deleteSelection().run()
        isExpandedEditor.value = false
        return true
      } else {
        return false
      }
    }

    function mentionMember () {
      if (mentionUser.value.length > 0 && selectedUsers.value.length > 0) {
        mentionUser.value.forEach(item => {
          if (selectedUsers.value.some(u => u.attrs?.id === item.id)) {
            bookService.mentionMember({
              bookId: item.bookId,
              memberId: item.id,
              chatId: item.chatId,
              message: message.value
            })
          }
        })

        mentionUser.value = []
        selectedUsers.value = []
      }
    }

    function uploadImage (event) {
      const hasFiles = event.target.files.length

      if (!hasFiles) return

      const images = Array
        .from(event.target.files)
        .filter(file => (/image/i).test(file.type))

      if (images.length === 0) return

      imageUploaded(images[0]).then(res => {
        editor.value.chain().focus().setImageChat(res).run()
      })

      event.srcElement.value = ''
    }

    function imageUploaded (file) {
      commit('loading', true)

      const formData = new FormData()
      formData.append('data', file)

      return chatService.uploadAttachment({
        chatId: props.chatId,
        data: formData
      })
        .then(response => {
          uploadedImages.push(response.data.id)
          return {
            url: response.data.url,
            id: response.data.id
          }
        })
    }

    function openImageUploader () {
      inputImageRef.value.click()
    }

    function insertEmoji (emoji) {
      if (emoji.category === 'flags') {
        editor.value.chain().focus().setImageChatEmoji({ url: require(`@/assets/images/icons/flags/${emoji.name}.png`) }).run()
      } else {
        const transaction = editor.value.state.tr.insertText(emoji.char)
        editor.value.view.dispatch(transaction)
      }
      editor.value.chain().focus()
      closeEmojiPicker()
    }

    function insertGif (gif) {
      editor.value.chain().focus().setGif({ url: gif }).run()
      closeGifPicker()
    }

    function openEmojiPicker () {
      showEmojiPicker.value = !showEmojiPicker.value
      showGifPicker.value = false

      setTimeout(() => {
        if (emojiPickerRef.value) emojiPickerRef.value.toggleInput()
      }, 100)
    }

    function openGifPicker () {
      showGifPicker.value = !showGifPicker.value
      showEmojiPicker.value = false

      setTimeout(() => {
        if (gifPickerRef.value) gifPickerRef.value.toggleInput()
      }, 100)
    }

    function closeEmojiPicker () {
      showEmojiPicker.value = false
    }

    function closeGifPicker () {
      showGifPicker.value = false
    }

    function onHover (e) {
      e.target.style = `background-color: ${designConfig.themes[settings.value.theme].selectionColor}`
    }

    function outHover (index, e) {
      e.target.style = 'background-color: transparent'
    }

    function expandEditor () {
      isExpandedEditor.value = !isExpandedEditor.value
      emit('scrollToDown')
    }

    function selectMention (item) {
      if (item.label === 'users') {
        mentionUser.value.push(item)
      }
      return mention.selectMention(item)
    }

    return {
      // ref
      inputImageRef,
      showEmojiPicker,
      showGifPicker,
      gifPickerRef,
      emojiPickerRef,
      isExpandedEditor,
      isFocusedEditor,
      // computed
      isMobile,
      isDarkMode,
      user,
      // editor
      editor,
      // function
      sendMessage,
      insertEmoji,
      insertGif,
      openEmojiPicker,
      openGifPicker,
      closeEmojiPicker,
      closeGifPicker,
      onHover,
      outHover,
      expandEditor,
      // emoji
      navigatedEmojiIndex: emoji.navigatedEmojiIndex,
      filteredItems: emoji.filteredItems,
      selectEmoji: emoji.selectEmoji,
      hasResults: emoji.hasResults,
      showEmoji: emoji.showEmoji,
      emojiRefs: emoji.emojiRefs,
      emojiRef: emoji.emojiRef,
      uploadImage,
      openImageUploader,
      // mention
      navigatedMentionIndex: mention.navigatedMentionIndex,
      filteredMentions: mention.filteredMentions,
      selectMention,
      showMentions: mention.showMentions,
      hasMentions: mention.hasMentions,
      mentionRefs: mention.mentionRefs,
      mentionRef: mention.mentionRef,

      mentionMember // used by ref
    }
  }
})
</script>
